<template>
  <div>
    <LoadingPage v-if="loadingUser" />

    <div v-else-if="userData && userData.user" class="customer-view-page">
      <div v-if="userData.isBetStopFound" class="watermark-text">
        BETSTOPPED
      </div>
      <h4 class="mb-1" style="font-weight: 700">
        Customer Details —
        <span class="text-primary"
          >{{ userData.user.firstName }} {{ userData.user.lastName }}</span
        >
      </h4>
      <div class="action-row mb-2">
        <b-button class="abtn" @click="handleOpenResetPasswordModal">
          <feather-icon icon="LockIcon" />
          <span>Reset Password</span>
        </b-button>
        <b-dropdown no-caret toggle-class="abtn abtn-orange">
          <template #button-content>
            <feather-icon icon="EyeIcon" />
            <span>Change Status</span>
          </template>
          <b-dropdown-item
            v-for="item in statuses"
            :key="item.value"
            @click="openStatusModal(item)"
          >
            {{ item.label }}
          </b-dropdown-item>
        </b-dropdown>
        <b-button class="abtn abtn-blue" @click="handleOpenBootsTokenModal">
          <feather-icon icon="ZapIcon" />
          <span>Apply Boost Tokens</span>
        </b-button>
        <b-button class="abtn abtn-blue" @click="handleOpenBetPromoModal">
          <feather-icon icon="ZapIcon" />
          <span>Apply Bet Promo</span>
        </b-button>
        <b-button class="abtn abtn-green" @click="handleOpenBonusCashModal">
          <feather-icon icon="PercentIcon" />
          <span>Apply Bonus Cash</span>
        </b-button>
        <b-button class="abtn abtn-red" @click="handleOpenDeductBonusModal">
          <feather-icon icon="PercentIcon" />
          <span>Deduct Bonus Cash</span>
        </b-button>
        <b-button class="abtn abtn-green" @click="handleOpenDepositMatchModal">
          <feather-icon icon="GiftIcon" />
          <span>Apply Deposit Match</span>
        </b-button>
        <b-button class="abtn abtn-teal" @click="handleOpenApplyGameModal">
          <feather-icon icon="MonitorIcon" />
          <span>Apply Game</span>
        </b-button>
        <b-link
          :to="{
            name: 'customers-edit',
            params: { id: userData.user.userId },
          }"
        >
          <b-button class="abtn abtn-blue">
            <feather-icon icon="EditIcon" />
            <span>Edit</span>
          </b-button>
        </b-link>
        <b-dropdown
          right
          no-caret
          variant="outline-secondary"
          toggle-class="abtn"
          menu-class="actions-menu"
        >
          <template #button-content>
            <span>Actions</span>
            <feather-icon icon="ChevronDownIcon" />
          </template>
          <b-dropdown-item @click="handleOpenBetstopModal">
            Check NSER Status
          </b-dropdown-item>
          <b-dropdown-item @click="openActivityLogModal">
            Activity Log
          </b-dropdown-item>
          <b-dropdown-item @click="openAuditLogModal">
            Audit Log
          </b-dropdown-item>
          <b-dropdown-divider />
          <b-dropdown-item
            v-for="flag in advanceFlags"
            :key="flag.field"
            :disabled="loadingAdvanceFlag"
            @click="handleToggleAdvanceFlag(flag)"
          >
            <div class="d-flex align-items-center justify-content-between">
              <span>{{ flag.label }}</span>
              <b-badge
                class="ml-1"
                :variant="flag.value ? 'light-success' : 'light-secondary'"
              >
                {{ flag.value ? "On" : "Off" }}
              </b-badge>
            </div>
            <small class="text-muted">
              Turn {{ flag.value ? "off" : "on" }}
            </small>
          </b-dropdown-item>
        </b-dropdown>
      </div>
      <div class="customer-profile-grid">
        <!-- LEFT: Customer Info card with Revenue -->
        <div>
          <CustomerInfo />
        </div>
        <!-- RIGHT: Sidebar -->
        <div class="customer-sidebar">
          <CurrentBalances />
          <FinancialStatus />
        </div>
      </div>
      <CustomerViewAccount :userData="userData" />

      <!-- MODALS -->
      <ResetPasswordModal :email="userData.user.email" />
      <ApplyBootsTokenModal />
      <BonusCashModal />
      <ApplyDepositMatchModal />
      <ApplyBetPromoModal />
      <ShortTermBreakModal />
      <LongTermBreakModal />
      <ReActiveAccountModal />
      <PermanentSelfExcludeModal />
      <ManualVerifyModal
        :email="userData.user.email"
        :phoneNumber="userData.user.phoneNumber"
        :fullName="`${userData.user.firstName} ${userData.user.lastName}`"
        :gender="userData.user.gender"
      />
      <NSERReActivateModal />
      <AdminBlockModal />
      <BetstopCheckModal />
      <ActivityLogModal />
      <AuditLogModal />
      <DeductBonusModal />
      <ApplyGameModal />
      <UnBlockAccountModal />
    </div>
  </div>
</template>

<script>
import Spinner from "@core/components/Spinner";
import {
  BCard,
  BButton,
  BCol,
  BForm,
  BFormGroup,
  BFormInput,
  BMedia,
  BRow,
  BFormCheckbox,
  BInputGroupAppend,
  BContainer,
  BCardText,
  BFormSelect,
  BFormTextarea,
  BBadge,
  BInputGroup,
  BTable,
  BCardBody,
  BLink,
  BPagination,
  BAvatar,
  BModal,
  BDropdown,
  BDropdownItem,
  BDropdownDivider,
} from "bootstrap-vue";
import flatPickr from "vue-flatpickr-component";

import LoadingPage from "@core/components/LoadingPage";
import { mapActions, mapState } from "vuex";
import CustomerViewAccount from "./CustomerViewAccount.vue";
import CustomerInfo from "./CustomerInfo.vue";
import vSelect from "vue-select";
import ResetPasswordModal from "./modals/ResetPasswordModal.vue";
import ApplyBootsTokenModal from "./modals/ApplyBootsTokenModal.vue";
import BonusCashModal from "./modals/BonusCashModal.vue";
import ApplyDepositMatchModal from "./modals/ApplyDepositMatchModal.vue";
import ApplyBetPromoModal from "./modals/ApplyBetPromoModal.vue";
import ShortTermBreakModal from "./modals/ShortTermBreakModal.vue";
import LongTermBreakModal from "./modals/LongTermBreakModal.vue";
import ReActiveAccountModal from "./modals/ReActiveAccountModal.vue";
import PermanentSelfExcludeModal from "./modals/PermanentSelfExcludeModal.vue";
import ManualVerifyModal from "./modals/ManualVerifyModal.vue";
import NSERReActivateModal from "./modals/NSERReActivateModal.vue";
import AdminBlockModal from "./modals/AdminBlockModal.vue";
import BetstopCheckModal from "./modals/BetstopCheckModal.vue";
import ActivityLogModal from "./modals/ActivityLogModal.vue";
import AuditLogModal from "./modals/AuditLogModal.vue";
import DeductBonusModal from "./modals/DeductBonusModal.vue";
import ApplyGameModal from "./modals/ApplyGameModal.vue";
import UnBlockAccountModal from "./modals/UnBlockAccountModal.vue";
import CurrentBalances from "./components/CurrentBalances.vue";
import FinancialStatus from "./components/FinancialStatus.vue";
import useToastHandler from "@/services/toastHandler";

export default {
  components: {
    CustomerViewAccount,
    CustomerInfo,
    CurrentBalances,
    FinancialStatus,
    vSelect,
    LoadingPage,
    Spinner,
    BCard,
    BButton,
    BCol,
    BForm,
    BFormGroup,
    BFormInput,
    BMedia,
    BRow,
    BFormCheckbox,
    BInputGroupAppend,
    BContainer,
    BCardText,
    BFormSelect,
    BFormTextarea,
    BBadge,
    BInputGroup,
    BTable,
    BCardBody,
    BLink,
    BPagination,
    BAvatar,
    BModal,
    flatPickr,
    Spinner,
    ResetPasswordModal,
    ApplyBootsTokenModal,
    BonusCashModal,
    ApplyDepositMatchModal,
    ApplyBetPromoModal,
    BDropdown,
    BDropdownItem,
    BDropdownDivider,
    ShortTermBreakModal,
    LongTermBreakModal,
    ReActiveAccountModal,
    PermanentSelfExcludeModal,
    ManualVerifyModal,
    NSERReActivateModal,
    AdminBlockModal,
    BetstopCheckModal,
    ActivityLogModal,
    AuditLogModal,
    DeductBonusModal,
    ApplyGameModal,
    UnBlockAccountModal,
  },
  data() {
    const statuses = [
      // { modalId: null, value: "active", label: "Active" },
      // { modalId: null, value: "short_break", label: "Short Break" },
      {
        modalId: "short-term-block-modal",
        value: "short_block",
        label: "Short Term Block",
      },
      {
        modalId: "long-term-block-modal",
        value: "long_block",
        label: "Long Term Block",
      },
      {
        modalId: "admin-block-modal",
        value: "admin_block",
        label: "Admin Block",
      },
      {
        modalId: "manual-verify-modal",
        value: "manual_verify",
        label: "Manual Verify",
      },
      {
        modalId: "nser-reactivate-modal",
        value: "nser_reactivate",
        label: "NSER Reactivate",
      },
      {
        modalId: "permanent-self-exclude-modal",
        value: "perm_exclude",
        label: "Permanent Self Exclude",
      },
      { modalId: "re-active-modal", value: "reactivate", label: "Re Activate" },
      { modalId: "un-block-modal", value: "unblock", label: "Unblock" },
    ];
    return {
      status: "",
      statuses,
      loadingAdvanceFlag: false,
    };
  },
  computed: {
    ...mapState({
      userData: (s) => s.user.detail.userData,
      loadingUser: (s) => s.user.detail.loading,
      stateAddress: (s) => s.user.newUserParams.state,
    }),
    advanceFlags() {
      const user = this.userData?.user || {};
      return [
        { field: "betPlacedSmsAlert", label: "Bet Placed SMS Alert" },
        {
          field: "withdrawalTwoFactorEnabled",
          label: "Withdrawal 2FA (SMS OTP)",
        },
      ].map((flag) => ({ ...flag, value: !!user[flag.field] }));
    },
  },
  mounted() {},
  async created() {
    await this.syncDetail();
    await this.getAvailableBonus(this.$router.currentRoute.params.id);
  },
  watch: {
    "$route.params.id": {
      handler: async function (newId) {
        console.log('object')
        await this.syncDetail();
        await this.getAvailableBonus(newId);
      },
    },
  },
  methods: {
    ...mapActions({
      fetchUser: "user/detail/asyncUser",
      addBonusForUser: "user/bonus/addBonusForUser",
      getAvailableBonus: "user/bonus/getAvailableBonus",
      checkBetstop: "user/detail/checkBetstop",
      updateClientProfile: "user/detail/updateClientProfile",
      updateUserAdvance: "user/detail/updateUserAdvance",
    }),
    async syncDetail() {
      await this.fetchUser({ id: this.$router.currentRoute.params.id });
    },

    handleOpenResetPasswordModal() {
      this.$bvModal.show("reset-password-modal");
    },
    handleOpenBootsTokenModal() {
      this.$bvModal.show("apply-boots-token-modal");
    },
    handleOpenBonusCashModal() {
      this.$bvModal.show("bonus-cash-modal");
    },
    handleOpenDepositMatchModal() {
      this.$bvModal.show("apply-deposit-match-modal");
    },
    handleOpenApplyGameModal() {
      this.$bvModal.show("apply-game-modal");
    },
    handleOpenBetPromoModal() {
      this.$bvModal.show("apply-bet-promo-modal");
    },
    openActivityLogModal() {
      this.$bvModal.show("activity-log-modal");
    },
    openAuditLogModal() {
      this.$bvModal.show("audit-log-modal");
    },
    async handleOpenBetstopModal() {
      this.$bvModal.show("check-betstop-modal");
      const res = await this.checkBetstop(this.$router.currentRoute.params.id);
      if (res && !res.verified && typeof res.verified === "boolean") {
        await this.updateClientProfile({
          userId: this.$router.currentRoute.params.id,
          isActive: false,
        });
        this.syncDetail();
      }
    },
    openStatusModal(item) {
      if (item.modalId) {
        this.$bvModal.show(item.modalId);
      }
    },
    handleOpenDeductBonusModal() {
      this.$bvModal.show("deduct-bonus-modal");
    },
    async handleToggleAdvanceFlag({ field, label, value }) {
      const nextValue = !value;
      const message = this.$createElement(
        "div",
        { style: { color: "#333", fontWeight: 500 } },
        `Are you sure you want to turn ${
          nextValue ? "on" : "off"
        } "${label}" for this user?`
      );
      const confirmed = await this.$bvModal.msgBoxConfirm(message, {
        title: label,
        okTitle: "Confirm",
        cancelTitle: "Cancel",
        centered: true,
      });
      if (!confirmed) return;
      this.loadingAdvanceFlag = true;
      try {
        const res = await this.updateUserAdvance({
          userId: this.$router.currentRoute.params.id,
          [field]: nextValue,
        });
        if (res) {
          this.showToastSuccess(
            "Success",
            `${label} turned ${nextValue ? "on" : "off"}`
          );
          await this.syncDetail();
        }
      } finally {
        this.loadingAdvanceFlag = false;
      }
    },
  },
  setup() {
    const { showToastSuccess } = useToastHandler();
    return { showToastSuccess };
  },
};
</script>
<style lang="scss">
@import "@core/scss/vue/libs/vue-flatpicker.scss";
@import "@core/scss/vue/libs/vue-select.scss";
/* === ACTION BUTTONS === */
.action-row {
  display: flex;
  gap: 6px;
  flex-wrap: wrap;
  padding: 0;
}

.action-row .btn.abtn,
.action-row .btn-group > .btn.abtn {
  padding: 5px 10px !important;
  border-radius: 4px !important;
  font-size: 11px !important;
  font-weight: 500 !important;
  border: 1px solid #ccc !important;
  background-color: #fff !important;
  color: #333 !important;
  white-space: nowrap;
  transition: all 0.15s;
  display: inline-flex;
  align-items: center;
  gap: 4px;
  height: auto !important;
  line-height: 1.4;
}

.action-row .btn.abtn:hover,
.action-row .btn-group > .btn.abtn:hover {
  background-color: #f0f4ff !important;
  border-color: #a0b4e8 !important;
  color: #333 !important;
}

.action-row .btn.abtn:focus,
.action-row .btn.abtn:active,
.action-row .btn-group > .btn.abtn:focus,
.action-row .btn-group > .btn.abtn:active {
  box-shadow: none !important;
  outline: none !important;
  background-color: #fff !important;
  color: #333 !important;
}

.action-row .btn.abtn svg,
.action-row .btn-group > .btn.abtn svg {
  width: 12px;
  height: 12px;
}

.action-row .btn.abtn-blue,
.action-row .btn-group > .btn.abtn-blue {
  background-color: #1a56db !important;
  color: #fff !important;
  border-color: #1a56db !important;
}
.action-row .btn.abtn-blue:hover,
.action-row .btn-group > .btn.abtn-blue:hover {
  background-color: #1545b5 !important;
  border-color: #1545b5 !important;
  color: #fff !important;
}
.action-row .btn.abtn-blue:focus,
.action-row .btn.abtn-blue:active,
.action-row .btn-group > .btn.abtn-blue:focus,
.action-row .btn-group > .btn.abtn-blue:active {
  background-color: #1a56db !important;
  color: #fff !important;
}

.action-row .btn.abtn-red,
.action-row .btn-group > .btn.abtn-red {
  background-color: #dc3545 !important;
  color: #fff !important;
  border-color: #dc3545 !important;
}
.action-row .btn.abtn-red:hover,
.action-row .btn-group > .btn.abtn-red:hover {
  background-color: #c82333 !important;
  border-color: #c82333 !important;
  color: #fff !important;
}
.action-row .btn.abtn-red:focus,
.action-row .btn.abtn-red:active,
.action-row .btn-group > .btn.abtn-red:focus,
.action-row .btn-group > .btn.abtn-red:active {
  background-color: #dc3545 !important;
  color: #fff !important;
}

.action-row .btn.abtn-green,
.action-row .btn-group > .btn.abtn-green {
  background-color: #198754 !important;
  color: #fff !important;
  border-color: #198754 !important;
}
.action-row .btn.abtn-green:hover,
.action-row .btn-group > .btn.abtn-green:hover {
  background-color: #157347 !important;
  border-color: #157347 !important;
  color: #fff !important;
}
.action-row .btn.abtn-green:focus,
.action-row .btn.abtn-green:active,
.action-row .btn-group > .btn.abtn-green:focus,
.action-row .btn-group > .btn.abtn-green:active {
  background-color: #198754 !important;
  color: #fff !important;
}

.action-row .btn.abtn-orange,
.action-row .btn-group > .btn.abtn-orange {
  background-color: #fd7e14 !important;
  color: #fff !important;
  border-color: #fd7e14 !important;
}
.action-row .btn.abtn-orange:hover,
.action-row .btn-group > .btn.abtn-orange:hover {
  background-color: #e96b02 !important;
  border-color: #e96b02 !important;
  color: #fff !important;
}
.action-row .btn.abtn-orange:focus,
.action-row .btn.abtn-orange:active,
.action-row .btn-group > .btn.abtn-orange:focus,
.action-row .btn-group > .btn.abtn-orange:active {
  background-color: #fd7e14 !important;
  color: #fff !important;
}

.action-row .btn.abtn-teal,
.action-row .btn-group > .btn.abtn-teal {
  background-color: #0d6efd !important;
  color: #fff !important;
  border-color: #0d6efd !important;
}
.action-row .btn.abtn-teal:hover,
.action-row .btn-group > .btn.abtn-teal:hover {
  background-color: #0b5ed7 !important;
  border-color: #0b5ed7 !important;
  color: #fff !important;
}
.action-row .btn.abtn-teal:focus,
.action-row .btn.abtn-teal:active,
.action-row .btn-group > .btn.abtn-teal:focus,
.action-row .btn-group > .btn.abtn-teal:active {
  background-color: #0d6efd !important;
  color: #fff !important;
}

/* === ACTIONS DROPDOWN === */
.action-row .actions-menu {
  min-width: 240px;
  font-size: 13px;
}
.action-row .actions-menu .dropdown-item {
  padding: 6px 14px;
}

/* Responsive - Tablet */
@media (max-width: 1199px) {
  .action-row {
    gap: 5px;
  }
  .action-row .btn.abtn,
  .action-row .btn-group > .btn.abtn {
    font-size: 10px !important;
    padding: 4px 8px !important;
  }
}

/* Responsive - Mobile */
@media (max-width: 767px) {
  .action-row {
    gap: 4px;
  }
  .action-row .btn.abtn,
  .action-row .btn-group > .btn.abtn {
    font-size: 10px !important;
    padding: 4px 6px !important;
    gap: 3px;
  }
}
/* === CUSTOMER PROFILE 2-COLUMN GRID === */
.customer-profile-grid {
  display: grid;
  grid-template-columns: minmax(0, 1fr) 320px;
  gap: 16px;
  align-items: start;
  margin-bottom: 1rem;
  max-width: 100%;
}

.customer-profile-grid > * {
  min-width: 0;
  max-width: 100%;
}

.customer-sidebar {
  display: flex;
  flex-direction: column;
  gap: 12px;
  min-width: 0;
}

@media (max-width: 1399px) {
  .customer-profile-grid {
    grid-template-columns: minmax(0, 1fr) 300px;
    gap: 12px;
  }
}

@media (max-width: 1199px) {
  .customer-profile-grid {
    grid-template-columns: minmax(0, 1fr) 280px;
    gap: 12px;
  }
}

@media (max-width: 991px) {
  .customer-profile-grid {
    grid-template-columns: 1fr;
  }
}

p {
  margin-bottom: 0;
}
.circle-cancel {
  position: absolute;
  top: -4px;
  right: -7px;
  border: 1px solid black;
  background: white;
  border-radius: 50%;
  width: 16px;
  height: 16px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
}

.dark-layout
  .flatpickr-calendar
  .flatpickr-days
  .flatpickr-day:hover:not(.selected):not(.today):not(.startRange):not(
    .endRange
  ) {
  background: #ccc;
}

/* === PAGE WRAPPER === */
.customer-view-page {
  max-width: 100%;
  overflow-x: hidden;
}

.customer-view-page > h4 {
  overflow-wrap: break-word;
  word-break: break-word;
}

@media (max-width: 767px) {
  .customer-view-page > h4 {
    font-size: 16px;
  }
}

.watermark-text {
  position: fixed;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%) rotate(-30deg);

  font-family: Arial, Helvetica, sans-serif;
  font-weight: 900;
  text-transform: uppercase;
  white-space: nowrap;

  color: rgba(255, 0, 0, 0.1);
  font-size: 15vw;

  z-index: 9999;
  pointer-events: none;
  user-select: none;
}
</style>
